import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidV4 } from 'uuid';
import dayjs from 'dayjs';

import useShoppingCartContext from '../../../../context/ShoppingCart/use-shopping-cart-context';

import { Disclaimer } from '../../../../components/Disclaimer';

import { FamilyPackTicketsSelect } from '../family-pack-tickets-select';
import { TravelDateSelect } from '../../../travels/components/travel-date-select';
import { TravelReasonSelect } from '../ticket-travel-reason-select';
import { TicketTravelPointsSwitch } from '../travel-points-switch';
import TravelPurchaseSummary from '../travel-purchase-summary';
import {
  DepartureDate,
  ReturnDate,
  TravelPoints,
  TravelType,
  isOneWayTrip,
  isRoundTrip,
} from '../../../../domain/travels';
import { TravelTypeSelect } from '../../../travels/components';
import {
  FamilyPackTicket,
  TicketCompany,
  TicketTravelReason,
  TicketType,
  getTicketCompanyDetails,
  getTicketTravelPoints,
} from '../../../../domain/tickets';
import { TicketsService } from '../../../../services/tickets';
import { useTicketsConfig } from '../../hooks';
import { GroupRate } from '../../../../types/Ticket';

interface Form {
  travelType: TravelType;
  travelPoints: TravelPoints<TicketCompany>;
  travelReason: TicketTravelReason | null;
  passengers: FamilyPackTicket | null;
  dates: {
    departureDate: DepartureDate | null;
    returnDate: ReturnDate | null;
  };
  total: string;
}

interface ValidForm extends Form {
  travelReason: TicketTravelReason;
  dates: {
    departureDate: DepartureDate;
    returnDate: ReturnDate | null;
  };
}

const DEFAULT_FORM_VALUES: Omit<Form, 'travelPoints'> = {
  travelType: TravelType.RoundTrip,
  // travelPoints: DEFAULT_TRAVEL_POINTS,
  travelReason: null,
  passengers: null,
  dates: {
    departureDate: null,
    returnDate: null,
  },
  total: '',
};

const isFormValid = (formValues: Form): formValues is ValidForm => {
  const { travelType, travelReason, dates, passengers } = formValues;

  if (!travelReason) return false;
  if (!dates.departureDate) return false;
  if (!passengers) return false;

  if (isRoundTrip(travelType) && !dates.returnDate) return false;

  return true;
};

interface Props {
  country: string;
  onSubmit?: () => void /** Exposes a callback to let the father component know when the form is submitted */;
}

export const GroupTicketPurchase = ({ country, onSubmit }: Props) => {
  const { t } = useTranslation();

  const { addServiceToCart, cart, openShoppingCart, openShoppingCartAddServices } = useShoppingCartContext();

  const { ticketsConfig } = useTicketsConfig();

  const defaultFormValues: Form = { ...DEFAULT_FORM_VALUES, travelPoints: getTicketTravelPoints(country) };
  const { control, formState, getValues, reset, setValue, watch, clearErrors } = useForm({ defaultValues: defaultFormValues });

  const calculateTicketPrice = useCallback(() => {
    const formValues = getValues();

    if (!isFormValid(formValues)) {
      setValue('total', '');
      return;
    }

    const response = TicketsService.calculateTicketPrice({
      company: formValues.travelPoints.origin,
      departureDate: formValues.dates.departureDate,
      returnDate: formValues.dates.returnDate ?? undefined,
      isFamily: true,
      isRoundTrip: formValues.travelType === TravelType.RoundTrip,
      packages: ticketsConfig,
      ticketType: TicketType.Family,
      passengers: formValues.passengers,
    });

    setValue('total', response.total);
  }, [ticketsConfig]);

  const buildTicket = (): void => {
    const formValues = getValues();

    if (!isFormValid(formValues)) return;

    const response = TicketsService.calculateTicketPrice({
      company: formValues.travelPoints.origin,
      departureDate: formValues.dates.departureDate,
      returnDate: formValues.dates.returnDate ?? undefined,
      isFamily: true,
      isRoundTrip: formValues.travelType === TravelType.RoundTrip,
      packages: ticketsConfig,
      ticketType: TicketType.Family,
      passengers: formValues.passengers,
    });

    const tickets = response.products.map((product) => {
      const amount = parseFloat(product.Amount.toFixed(2));
      const item: GroupRate = {
        // id: cart.items.length + '',
        id: uuidV4(),
        ticketTier: 'Family',
        origin: getTicketCompanyDetails(formValues.travelPoints.origin).place,
        destination: getTicketCompanyDetails(formValues.travelPoints.destination).place,
        departureDate: new Date(`${formValues.dates.departureDate}T12:00:00.000Z`),
        returnDate: formValues.dates.returnDate ? new Date(`${formValues.dates.returnDate}T12:00:00.000Z`) : undefined,
        quantity: product.NuProdQuant,
        tripType: formValues.travelType,
        visitReason: formValues.travelReason,
        price: amount,
        total: parseFloat((amount * product.NuProdQuant).toFixed(2)),
        type: 'ticket',
        tixProd: product,
      };
      return item;
    });

    tickets.forEach((ticket) => {
      addServiceToCart(ticket, 'SingleTicket');
    });

    reset(); /** Clear form after adding tickets to the cart */

    onSubmit?.(); /** Exposes a callback to let the father component know when the form is submitted */

    if (
      openShoppingCartAddServices ||
      (typeof window !== 'undefined' && !window.location.pathname.includes('/checkout'))
    ) {
      openShoppingCart();
    }
  };
  const handleTravelTypeChange = (onChange: (type: TravelType) => void) => (type: TravelType) => {
    onChange(type);
    calculateTicketPrice();
  };

  const handleTravelPointsSwitch =
    (onChange: (travelPoints: TravelPoints<TicketCompany>) => void) => (travelPoints: TravelPoints<TicketCompany>) => {
      onChange(travelPoints);
      calculateTicketPrice();
    };

  const handleDatesChange =
    (onChange: (dates: { departureDate: DepartureDate | null; returnDate: ReturnDate | null }) => void) =>
      (departureDate: DepartureDate | null, returnDate: ReturnDate | null) => {
        onChange({ departureDate, returnDate });

        if (returnDate && dayjs(returnDate).isBefore(dayjs(departureDate))) {
          control.setError('dates.returnDate', { message: t('tickets_service.errors.return_date_invalid') });
        } else if (dayjs(departureDate).isBefore(dayjs(), 'day')) {
          control.setError('dates.departureDate', { message: t('tickets_service.errors.departure_date_invalid')});
        } else {
          clearErrors('dates.returnDate');
          clearErrors('dates.departureDate');
        }

        calculateTicketPrice();
      };

  const handlePassengersChange =
    (onChange: (familyPackTicket: FamilyPackTicket) => void) => (familyPackTicket: FamilyPackTicket) => {
      onChange(familyPackTicket);
      calculateTicketPrice();
    };

  const handleTravelReasonChange = (onChange: (reason: TicketTravelReason) => void) => (reason: TicketTravelReason) => {
    onChange(reason);
    calculateTicketPrice();
  };

  return (
    <Stack gap={3}>
      <Controller
        control={control}
        name="travelType"
        render={({ field }) => (
          <TravelTypeSelect travelType={field.value} onTravelTypeChange={handleTravelTypeChange(field.onChange)} />
        )}
      />
      <Disclaimer variant="important">
        <Disclaimer.Content>
          {t('tickets_service.group_rates.whole_group_must_be_present_at_e_gates')}
        </Disclaimer.Content>
      </Disclaimer>
      <Controller
        control={control}
        name="travelPoints"
        render={({ field }) => <TicketTravelPointsSwitch origin={field.value.origin} destination={field.value.destination} onSwitch={handleTravelPointsSwitch(field.onChange)} />}
      />
      <Controller
        control={control}
        name="dates"
         rules={{ required: true, validate: (value) => {
          if (dayjs(value.returnDate).isBefore(dayjs(value.departureDate), "day") || dayjs(value.departureDate).isBefore(dayjs(), "day")) {
            return false;
          }
          return true;
        },
        }}
        render={({ field, fieldState }) => (
          <TravelDateSelect
            departureDate={field.value.departureDate}
            returnDate={field.value.returnDate}
            isReturnDateDisabled={isOneWayTrip(watch('travelType'))}
            onDateChange={handleDatesChange(field.onChange)}
            departureDateError={fieldState?.error?.departureDate}
            returnDateError={fieldState?.error?.returnDate}
          />
        )}
      />
      <Stack width="100%" direction="row" flexWrap="wrap" gap={3}>
        <Controller
          control={control}
          name="passengers"
          render={({ field }) => (
            <FamilyPackTicketsSelect value={field.value} onChange={handlePassengersChange(field.onChange)} />
          )}
        />
        <Controller
          control={control}
          name="travelReason"
          render={({ field }) => (
            <TravelReasonSelect value={field.value} onChange={handleTravelReasonChange(field.onChange)} />
          )}
        />
      </Stack>
      <Controller
        control={control}
        name="total"
        render={({ field }) => <TravelPurchaseSummary total={field.value} isValid={formState.isValid} onAddToShoppingCartClick={buildTicket} />}
      />
    </Stack>
  );
};
